import { Component } from "@theme/component";
import { sectionRenderer } from "@theme/section-renderer";
import { debounce } from "@theme/utilities";

/**
 * Searchable / filterable color-variant grid.
 *
 * Config (JSON via <script type="application/json" ref="config">):
 *   { "groupHandle": string, "sectionId": string, "totalProducts": number, "maxPages": number }
 *
 * Refs: config, search, select, grid, status
 */
class KleurgroepSelector extends Component {
  requiredRefs = ["grid"];

  #config = { groupHandle: "", sectionId: "", totalProducts: 0, maxPages: 5, currentKleurgroep: "" };
  #assembled = false;     // whether the full set has been fetched
  #assembling = false;
  #assemblePromise = null;
  #groupSelectionTouched = false;

  connectedCallback() {
    super.connectedCallback();
    try {
      this.#config = { ...this.#config, ...JSON.parse(this.refs.config?.textContent ?? "{}") };
    } catch (e) {
      console.warn("kleurgroep-selector: invalid config", e);
    }
    // If the server could not render the whole group, assemble it on first interaction.
    this.#needsFetch =
      this.#config.totalProducts > this.#renderedCount() && !!this.#config.groupHandle;
    this.#syncGroupOptions();
    this.#applyFilter();
    if (this.#needsFetch && this.refs.select) {
      this.#scheduleAssembly();
    }
  }

  #needsFetch = false;

  /** @returns {number} number of cards currently in the grid */
  #renderedCount() {
    return this.refs.grid?.querySelectorAll(".kleurgroep-grid__item").length ?? 0;
  }

  /** Declarative `on:input="filter"` on the search field. */
  filter = debounce(async () => {
    // Typing clears the kleurgroep select (Woo behavior).
    const term = (this.refs.search?.value ?? "").trim().toLowerCase();
    if (term && this.refs.select) {
      this.refs.select.value = "";
      this.#groupSelectionTouched = true;
    }
    await this.#ensureAssembled();
    this.#applyFilter();
  }, 250);

  /** Declarative `on:change="filterByGroup"` on the kleurgroep select. */
  filterByGroup = async () => {
    this.#groupSelectionTouched = true;
    await this.#ensureAssembled();
    this.#applyFilter();
  };

  /** Fetch all remaining pages once, append them to the grid, then mark assembled. */
  async #ensureAssembled() {
    if (this.#assembled || !this.#needsFetch) return;
    if (this.#assembling) return this.#assemblePromise;
    this.#assembling = true;
    this.#setStatus("loading");

    this.#assemblePromise = (async () => {
      const grid = this.refs.grid;
      const totalPages = Number(grid?.dataset.totalPages ?? "1");
      const startPage = Number(grid?.dataset.currentPage ?? "1") + 1;
      const lastPage = Math.min(totalPages, this.#config.maxPages);

      for (let page = startPage; page <= lastPage; page++) {
        const url = new URL(`/collections/${this.#config.groupHandle}`, window.location.origin);
        url.searchParams.set("page", String(page));
        const html = await sectionRenderer.getSectionHTML(this.#config.sectionId, true, url);
        const fragment = new DOMParser().parseFromString(html, "text/html");
        const fetchedGrid = fragment.querySelector('[ref="grid"]');
        if (!fetchedGrid) continue;
        grid.append(...fetchedGrid.querySelectorAll(".kleurgroep-grid__item"));
      }

      if (totalPages > this.#config.maxPages) {
        console.warn(
          `kleurgroep-selector: group has ${totalPages} pages; capped at ${this.#config.maxPages}.`
        );
      }
      this.#syncGroupOptions();
      this.#applyFilter();
      this.#assembled = true;
      this.#needsFetch = false;
    })();

    try {
      await this.#assemblePromise;
    } catch (e) {
      console.error("kleurgroep-selector: fetch failed", e);
    } finally {
      this.#assembling = false;
      this.#assemblePromise = null;
      this.#setStatus("idle");
    }
  }

  #scheduleAssembly() {
    const loadOptions = () => this.#ensureAssembled();
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(loadOptions, { timeout: 1500 });
      return;
    }
    window.setTimeout(loadOptions, 0);
  }

  #syncGroupOptions() {
    const select = this.refs.select;
    if (!select) return;

    const currentValue =
      select.value ||
      (!this.#groupSelectionTouched ? (this.#config.currentKleurgroep ?? "").trim().toLowerCase() : "");
    const allGroupsLabel = select.options[0]?.textContent ?? "Alle kleurgroepen";
    const groups = new Set();
    const cards = this.refs.grid?.querySelectorAll(".kleurgroep-grid__item") ?? [];

    for (const card of cards) {
      const group = (card.dataset.kleurgroep ?? "").trim().toLowerCase();
      if (group) groups.add(group);
    }

    const sortedGroups = [...groups].sort((a, b) =>
      a.localeCompare(b, document.documentElement.lang || undefined, { sensitivity: "base" })
    );

    if (sortedGroups.length === 0 && select.options.length > 1) {
      return;
    }

    const options = [new Option(allGroupsLabel, "")];

    for (const group of sortedGroups) {
      options.push(new Option(this.#formatGroupLabel(group), group));
    }

    select.replaceChildren(...options);
    select.value = currentValue && groups.has(currentValue) ? currentValue : "";

    if (this.refs.groupField) {
      this.refs.groupField.hidden = sortedGroups.length === 0;
    }
  }

  #formatGroupLabel(group) {
    return group.charAt(0).toUpperCase() + group.slice(1);
  }

  /** Show/hide cards based on the current search term + selected kleurgroep. */
  #applyFilter() {
    const term = (this.refs.search?.value ?? "").trim().toLowerCase();
    const group = (this.refs.select?.value ?? "").trim().toLowerCase();
    const cards = this.refs.grid?.querySelectorAll(".kleurgroep-grid__item") ?? [];
    let visible = 0;

    for (const card of cards) {
      const colorNumber = card.dataset.colorNumber ?? "";
      const title = (card.getAttribute("title") ?? "").toLowerCase();
      const cardGroup = card.dataset.kleurgroep ?? "";
      const matchesTerm = !term || colorNumber.includes(term) || title.includes(term);
      const matchesGroup = !group || cardGroup === group;
      const show = matchesTerm && matchesGroup;
      card.hidden = !show;
      if (show) visible++;
    }
    this.#setStatus(visible === 0 ? "empty" : "idle");
  }

  /** @param {"idle"|"loading"|"empty"} state */
  #setStatus(state) {
    const status = this.refs.status;
    if (!status) return;
    status.dataset.state = state;
  }
}

if (!customElements.get("kleurgroep-selector")) {
  customElements.define("kleurgroep-selector", KleurgroepSelector);
}
